import React, { useState } from "react";
import { Box, Paper, Stack, Button, TextField, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Sidebar from "../../components/Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { issueGatewayRegistrationLink } from "../../services/gatewayService";

const IssueRegLinkPage = () => {
  const { user, isLoggedIn } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [ttlMinutes, setTtlMinutes] = useState(15);
  const [maxUses, setMaxUses] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const copy = async (text, successKey) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(t(successKey), { variant: "success" });
    } catch {
      enqueueSnackbar(t("IssueRegLinkPage.snackbar.copyFailed"), { variant: "error" });
    }
  };

  const handleIssue = async () => {
    try {
      setLoading(true);

      const ttl = parseInt(ttlMinutes, 10);
      const max = maxUses === "" ? 1 : parseInt(maxUses, 10);

      if (!Number.isFinite(ttl) || ttl <= 0) {
        enqueueSnackbar(t("IssueRegLinkPage.errors.ttlPositive"), { variant: "error" });
        return;
      }

      if (!Number.isFinite(max) || max <= 0) {
        enqueueSnackbar(t("IssueRegLinkPage.errors.maxUsesPositive"), { variant: "error" });
        return;
      }

      const res = await issueGatewayRegistrationLink({
        ttl_minutes: ttl,
        max_uses: max,
      });

      if (!res.success) {
        enqueueSnackbar(res.error || t("IssueRegLinkPage.errors.issueFailed"), { variant: "error" });
        return;
      }

      setResult(res.data);
      enqueueSnackbar(t("IssueRegLinkPage.snackbar.issued"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("IssueRegLinkPage.errors.issueFailed"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  const token = result?.token;
  const registrationUrl = result?.registration_url;

  return (
    <>
      <Sidebar user={user} />
      <Box display="flex" justifyContent="center" p={2}>
        <Paper sx={{ width: "100%", maxWidth: 720, p: 3 }}>
          <Typography variant="h6" mb={1}>
            {t("IssueRegLinkPage.title")}
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("IssueRegLinkPage.subtitle")}
          </Typography>

          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("IssueRegLinkPage.fields.ttlMinutes")}
                type="number"
                value={ttlMinutes}
                onChange={(e) => setTtlMinutes(e.target.value)}
                fullWidth
              />
              <TextField
                label={t("IssueRegLinkPage.fields.maxUses")}
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                fullWidth
              />
            </Stack>

            <Button variant="contained" onClick={handleIssue} disabled={loading}>
              {t("IssueRegLinkPage.actions.issue")}
            </Button>

            {result && (
              <>
                <Divider />
                <Typography variant="subtitle1">
                  {t("IssueRegLinkPage.result.title")}
                </Typography>

                {registrationUrl && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("IssueRegLinkPage.result.registrationUrl.title")}
                    </Typography>

                    <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                      {registrationUrl}
                    </Typography>

                    <Stack direction="row" spacing={1} mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copy(registrationUrl, "IssueRegLinkPage.snackbar.urlCopied")}
                      >
                        {t("IssueRegLinkPage.actions.copyUrl")}
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => window.open(registrationUrl, "_blank")}
                      >
                        {t("IssueRegLinkPage.actions.open")}
                      </Button>
                    </Stack>
                  </Paper>
                )}

                {token && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("IssueRegLinkPage.result.token.title")}
                    </Typography>

                    <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                      {token}
                    </Typography>

                    <Stack direction="row" spacing={1} mt={2}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copy(token, "IssueRegLinkPage.snackbar.tokenCopied")}
                      >
                        {t("IssueRegLinkPage.actions.copyToken")}
                      </Button>
                    </Stack>
                  </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("IssueRegLinkPage.result.raw.title")}
                  </Typography>

                  <pre style={{ margin: 0, overflowX: "auto" }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </Paper>
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default IssueRegLinkPage;
